

// hooks/useFeatureLicense.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/Auth';

const DEFAULT_FEATURE_FLAGS = {
  is_google_map_enabled: false,
  is_echo_enabled: false,
  is_ai_email_enabled: false,
  automations: false,
  custom_objects: false,
};

function normalizeFeatures(data = {}) {
  const mergedFlags = { ...DEFAULT_FEATURE_FLAGS };
  const apiFeatures = Array.isArray(data.features) ? data.features : [];

  apiFeatures.forEach((item = {}) => {
    if (!item.key) return;
    mergedFlags[item.key] = Boolean(item.enabled);
  });

  // Legacy top-level booleans for backward compatibility
  ['is_google_map_enabled', 'is_echo_enabled', 'is_ai_email_enabled'].forEach((key) => {
    if (typeof data[key] === 'boolean') {
      mergedFlags[key] = data[key];
    }
  });

  return { mergedFlags, apiFeatures };
}

export function useFeatureLicense() {
  const {
    axiosInstance,
    token,
    isAuthReady,
    isAuthenticated,
  } = useAuth();
  const [features, setFeatures] = useState(DEFAULT_FEATURE_FLAGS);
  const [featureList, setFeatureList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeatures = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated || !axiosInstance || !token) {
      setFeatures(DEFAULT_FEATURE_FLAGS);
      setFeatureList([]);
      setLoading(!isAuthReady);
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get('/feature-licenses');
      const { mergedFlags, apiFeatures } = normalizeFeatures(response.data || {});
      setFeatures(mergedFlags);
      setFeatureList(apiFeatures);
    } catch (error) {
      console.error('Error fetching feature licenses:', error);
      setFeatures(DEFAULT_FEATURE_FLAGS);
      setFeatureList([]);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAuthReady, isAuthenticated, token]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return { features, featureList, loading, refetch: fetchFeatures };
}
